import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { DOMSerializer, Mark as ProseMirrorMark, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state'
import { AddMarkStep, RemoveMarkStep, ReplaceStep } from '@tiptap/pm/transform'
import { EditorView } from '@tiptap/pm/view'
import {
  ColorChange,
  FormattingChanges,
  ListChange,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_FORMATTING,
  SUGGESTION_TYPE_INSERTION,
  SuggestionBaseAttrs,
  SuggestionType,
  TEXT_STYLE_MARK_NAME,
} from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { generatePlaceholderId } from '../../../utils'
import { combineListChanges } from './list-track-changes'

const NON_FORMATTING_MARK_NAMES = new Set([SUGGESTION_MARK_NAME, 'chunk', TEXT_STYLE_MARK_NAME])

type FormattingChange = { from: number; to: number; markName: string; isAdd: boolean }

export { SuggestionType } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'

export const trackChangesPluginKey = new PluginKey('trackChanges')

export const TRACK_CHANGES_SKIP_META = 'skip'

export type TrackChangesUser = {
  id: number
  name: string
}

export type TrackChangesPluginOptions = {
  getEnabled: () => boolean
  getCurrentUser: () => TrackChangesUser | null
}

type DeletionRange = {
  from: number
  to: number
  cursorAfter: number
}

function createBaseAttrs(user: TrackChangesUser): SuggestionBaseAttrs {
  return {
    suggestionId: generatePlaceholderId(),
    authorId: user.id,
    authorName: user.name,
    createdAt: new Date().toISOString(),
  }
}

function readBaseAttrs(mark: ProseMirrorMark): SuggestionBaseAttrs {
  return {
    suggestionId: mark.attrs['suggestionId'] as string,
    authorId: mark.attrs['authorId'] as number,
    authorName: mark.attrs['authorName'] as string,
    createdAt: mark.attrs['createdAt'] as string,
  }
}

function findAdjacentSuggestionAttrs(doc: ProseMirrorNode, from: number, to: number, userId: number): SuggestionBaseAttrs | null {
  const beforeMark = doc.resolve(from).nodeBefore?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['authorId'] === userId) ?? null
  if (beforeMark !== null) {
    return readBaseAttrs(beforeMark)
  }

  const afterMark = doc.resolve(to).nodeAfter?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['authorId'] === userId) ?? null
  if (afterMark !== null) {
    return readBaseAttrs(afterMark)
  }

  return null
}

// Reuses the suggestion ID of the first same-author suggestion mark found anywhere
// inside [from, to]. Lets a list-change suggestion fold into a pre-existing text
// suggestion in the same paragraph, so "type then indent" surfaces as one combined
// card rather than two siblings with different IDs.
function findFirstSuggestionAttrsInRange(doc: ProseMirrorNode, from: number, to: number, userId: number): SuggestionBaseAttrs | null {
  let found: SuggestionBaseAttrs | null = null
  doc.nodesBetween(from, to, (node) => {
    if (found !== null) {
      return false
    }
    if (!node.isText) {
      return true
    }
    const mark = node.marks.find((candidate) => candidate.type.name === SUGGESTION_MARK_NAME && candidate.attrs['authorId'] === userId) ?? null
    if (mark !== null) {
      found = readBaseAttrs(mark)
    }
    return false
  })
  return found
}

function buildSuggestionAttrs(type: SuggestionType, baseAttrs: SuggestionBaseAttrs): Record<string, unknown> {
  return { ...baseAttrs, type }
}

export function rangeIsInsideInsertion(doc: ProseMirrorNode, from: number, to: number): boolean {
  let allInsertion = true
  let foundAnyText = false

  doc.nodesBetween(from, to, (node) => {
    if (!node.isText) {
      return true
    }

    foundAnyText = true
    const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null

    if (suggestionMark === null || suggestionMark.attrs['type'] !== SUGGESTION_TYPE_INSERTION) {
      allInsertion = false
      return false
    }

    return true
  })

  return foundAnyText && allInsertion
}

function rangeIsInsideTextSuggestion(doc: ProseMirrorNode, from: number, to: number): boolean {
  let allInside = true
  let foundAnyText = false

  doc.nodesBetween(from, to, (node) => {
    if (!node.isText) {
      return true
    }

    foundAnyText = true
    const hasTextSuggestion = node.marks.some(
      (mark) => mark.type.name === SUGGESTION_MARK_NAME && (mark.attrs['type'] === SUGGESTION_TYPE_INSERTION || mark.attrs['type'] === SUGGESTION_TYPE_DELETION)
    )
    if (!hasTextSuggestion) {
      allInside = false
      return false
    }
    return true
  })

  return foundAnyText && allInside
}

function findFormattingSuggestionAtExactRange(
  doc: ProseMirrorNode,
  from: number,
  to: number,
  userId: number
): { mark: ProseMirrorMark; baseAttrs: SuggestionBaseAttrs; formattingChanges: FormattingChanges } | null {
  // The candidate mark must (a) cover every text node in [from, to] and (b) not extend
  // past those bounds — otherwise merging would silently widen its formatting scope.
  const candidates: ProseMirrorMark[] = []
  let coversWholeRange = true

  doc.nodesBetween(from, to, (node) => {
    if (!coversWholeRange) {
      return false
    }
    if (!node.isText) {
      return true
    }
    const formattingMark =
      node.marks.find(
        (mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['type'] === SUGGESTION_TYPE_FORMATTING && mark.attrs['authorId'] === userId
      ) ?? null
    if (formattingMark === null) {
      coversWholeRange = false
      return false
    }
    candidates.push(formattingMark)
    return false
  })

  if (!coversWholeRange || candidates.length === 0) {
    return null
  }

  const candidate = candidates[0]
  const targetSuggestionId = candidate.attrs['suggestionId']
  if (!candidates.every((mark) => mark.attrs['suggestionId'] === targetSuggestionId)) {
    return null
  }

  // Reject when the suggestion's range extends past the new change's bounds — merging
  // would silently widen its scope. Checking the immediate neighbour text nodes is enough
  // because PM normalises adjacent text nodes carrying equal marks into one node.
  const beforeNode = doc.resolve(from).nodeBefore ?? null
  if (beforeNode !== null && beforeNode.marks.some((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['suggestionId'] === targetSuggestionId)) {
    return null
  }
  const afterNode = doc.resolve(to).nodeAfter ?? null
  if (afterNode !== null && afterNode.marks.some((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['suggestionId'] === targetSuggestionId)) {
    return null
  }

  return {
    mark: candidate,
    baseAttrs: {
      suggestionId: candidate.attrs['suggestionId'] as string,
      authorId: candidate.attrs['authorId'] as number,
      authorName: candidate.attrs['authorName'] as string,
      createdAt: candidate.attrs['createdAt'] as string,
    },
    formattingChanges: (candidate.attrs['formattingChanges'] as FormattingChanges | null) ?? { added: [], removed: [] },
  }
}

export function mergeColorChanges(existing: ColorChange | undefined, incoming: ColorChange | undefined): ColorChange | undefined {
  if (incoming === undefined) {
    return existing
  }
  const from = existing !== undefined ? existing.from : incoming.from
  if (from === incoming.to) {
    return undefined
  }
  return { from, to: incoming.to }
}

export type FormattingChangeInput = {
  added: Set<string>
  removed: Set<string>
  listChanges?: ListChange[]
  colorChange?: ColorChange
}

export function mergeFormattingChanges(existing: FormattingChanges, change: FormattingChangeInput): FormattingChanges | null {
  const added = new Set(existing.added)
  const removed = new Set(existing.removed)

  for (const markName of change.added) {
    if (removed.has(markName)) {
      removed.delete(markName)
    } else {
      added.add(markName)
    }
  }
  for (const markName of change.removed) {
    if (added.has(markName)) {
      added.delete(markName)
    } else {
      removed.add(markName)
    }
  }

  const incomingListChanges = change.listChanges ?? []
  const mergedListChanges = combineListChanges(existing.listChanges ?? [], incomingListChanges)
  const mergedColorChange = mergeColorChanges(existing.colorChange, change.colorChange)

  if (added.size === 0 && removed.size === 0 && mergedListChanges.length === 0 && mergedColorChange === undefined) {
    return null
  }

  const result: FormattingChanges = { added: Array.from(added), removed: Array.from(removed) }
  if (mergedListChanges.length > 0) {
    result.listChanges = mergedListChanges
  }
  if (mergedColorChange !== undefined) {
    result.colorChange = mergedColorChange
  }
  return result
}

export function applyFormattingSuggestion(tr: Transaction, range: { from: number; to: number }, user: TrackChangesUser, change: FormattingChangeInput): void {
  const { from, to } = range
  const skip = () => tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)

  if (rangeIsInsideTextSuggestion(tr.doc, from, to)) {
    skip()
    return
  }

  const suggestionMarkType = tr.doc.type.schema.marks[SUGGESTION_MARK_NAME] ?? null
  if (suggestionMarkType === null) {
    skip()
    return
  }

  const existing = findFormattingSuggestionAtExactRange(tr.doc, from, to, user.id)
  if (existing !== null) {
    const merged = mergeFormattingChanges(existing.formattingChanges, change)
    tr.removeMark(from, to, existing.mark)
    if (merged !== null) {
      const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, existing.baseAttrs), formattingChanges: merged }
      tr.addMark(from, to, suggestionMarkType.create(attrs))
    }
    skip()
    return
  }

  const baseAttrs =
    findFirstSuggestionAttrsInRange(tr.doc, from, to, user.id) ?? findAdjacentSuggestionAttrs(tr.doc, from, to, user.id) ?? createBaseAttrs(user)
  const formatting: FormattingChanges = { added: Array.from(change.added), removed: Array.from(change.removed) }
  if (change.listChanges !== undefined && change.listChanges.length > 0) {
    formatting.listChanges = change.listChanges
  }
  if (change.colorChange !== undefined) {
    formatting.colorChange = change.colorChange
  }
  const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, baseAttrs), formattingChanges: formatting }
  tr.addMark(from, to, suggestionMarkType.create(attrs))
  skip()
}

function markRangeAsDeletion(view: EditorView, range: DeletionRange, options: TrackChangesPluginOptions): boolean {
  const user = options.getCurrentUser()
  if (user === null) {
    return false
  }

  const suggestionMarkType = view.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
  if (suggestionMarkType === null) {
    return false
  }

  const baseAttrs = findAdjacentSuggestionAttrs(view.state.doc, range.from, range.to, user.id) ?? createBaseAttrs(user)

  const transaction = view.state.tr
  transaction.addMark(range.from, range.to, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_DELETION, baseAttrs)))

  const cursorPosition = Math.min(range.cursorAfter, transaction.doc.content.size)
  const resolvedCursor = transaction.doc.resolve(cursorPosition)
  transaction.setSelection(TextSelection.between(resolvedCursor, resolvedCursor))

  transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
  view.dispatch(transaction)
  return true
}

function computeDeletionRange(view: EditorView, key: 'Backspace' | 'Delete'): DeletionRange | null {
  const { state } = view
  const { from, to } = state.selection

  if (from !== to) {
    return { from, to, cursorAfter: from }
  }

  const resolvedFrom = state.doc.resolve(from)

  if (key === 'Backspace') {
    // At the start of a block (including the very start of the doc): nothing to mark.
    // Let the default handler perform any structural action (lift/join an empty block, etc.).
    if (resolvedFrom.parentOffset === 0) {
      return null
    }
    return { from: from - 1, to: from, cursorAfter: from - 1 }
  }

  if (resolvedFrom.parentOffset === resolvedFrom.parent.content.size) {
    return null
  }
  return { from, to: from + 1, cursorAfter: from }
}

function interceptDeletion(view: EditorView, key: 'Backspace' | 'Delete', options: TrackChangesPluginOptions): boolean {
  const range = computeDeletionRange(view, key)
  if (range === null) {
    return false
  }

  if (rangeIsInsideInsertion(view.state.doc, range.from, range.to)) {
    return false
  }

  return markRangeAsDeletion(view, range, options)
}

export function createTrackChangesPlugin(options: TrackChangesPluginOptions): Plugin {
  return new Plugin({
    key: trackChangesPluginKey,

    appendTransaction(transactions, _oldState, newState) {
      if (!options.getEnabled()) {
        return null
      }

      const insertions: { from: number; to: number }[] = []
      const formattingChanges: FormattingChange[] = []

      for (const transaction of transactions) {
        if (transaction.getMeta(trackChangesPluginKey) === TRACK_CHANGES_SKIP_META) {
          continue
        }
        // Remote Y.js sync transactions describe edits made by other clients — they
        // are NOT this user's edits and must not be wrapped as their suggestions.
        if (isChangeOrigin(transaction)) {
          continue
        }
        if (!transaction.docChanged) {
          continue
        }

        for (let stepIndex = 0; stepIndex < transaction.steps.length; stepIndex++) {
          const step = transaction.steps[stepIndex]
          // The mapping after this step (within this transaction) carries each step's range
          // forward through any subsequent steps so positions land in newState.
          const remainingMapping = transaction.mapping.slice(stepIndex + 1)

          if (step instanceof ReplaceStep && step.slice.size > 0) {
            const fromInNew = remainingMapping.map(step.from, 1)
            const toInNew = remainingMapping.map(step.from + step.slice.size, -1)
            if (fromInNew < toInNew) {
              insertions.push({ from: fromInNew, to: toInNew })
            }
            continue
          }

          if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
            const markName = step.mark.type.name
            if (NON_FORMATTING_MARK_NAMES.has(markName)) {
              continue
            }
            const fromInNew = remainingMapping.map(step.from, 1)
            const toInNew = remainingMapping.map(step.to, -1)
            if (fromInNew < toInNew) {
              formattingChanges.push({ from: fromInNew, to: toInNew, markName, isAdd: step instanceof AddMarkStep })
            }
          }
        }
      }

      if (insertions.length === 0 && formattingChanges.length === 0) {
        return null
      }

      const user = options.getCurrentUser()
      if (user === null) {
        return null
      }

      const suggestionMarkType = newState.schema.marks[SUGGESTION_MARK_NAME] ?? null
      if (suggestionMarkType === null) {
        return null
      }

      const transaction = newState.tr

      for (const { from, to } of insertions) {
        // Probe `transaction.doc` (not `newState.doc`) so subsequent insertions in this batch
        // can merge with the suggestionId we just assigned to an earlier adjacent range.
        const baseAttrs = findAdjacentSuggestionAttrs(transaction.doc, from, to, user.id) ?? createBaseAttrs(user)
        transaction.addMark(from, to, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_INSERTION, baseAttrs)))
      }

      // Group formatting changes by range so that toggling bold + italic in one transaction
      // produces a single combined suggestion rather than two overlapping ones.
      const formattingByRange = new Map<string, { from: number; to: number; added: Set<string>; removed: Set<string> }>()
      for (const change of formattingChanges) {
        const key = `${change.from}-${change.to}`
        const existing = formattingByRange.get(key) ?? null
        const entry = existing ?? { from: change.from, to: change.to, added: new Set<string>(), removed: new Set<string>() }
        if (existing === null) {
          formattingByRange.set(key, entry)
        }
        if (change.isAdd) {
          entry.added.add(change.markName)
        } else {
          entry.removed.add(change.markName)
        }
      }

      for (const { from, to, added, removed } of formattingByRange.values()) {
        // Skip when the range is already covered by an insertion/deletion suggestion — the
        // formatting just rides along with that pending text change. Existing formatting
        // suggestions are NOT skipped here; we want to merge with them below.
        if (rangeIsInsideTextSuggestion(transaction.doc, from, to)) {
          continue
        }

        const existing = findFormattingSuggestionAtExactRange(transaction.doc, from, to, user.id)
        if (existing !== null) {
          const merged = mergeFormattingChanges(existing.formattingChanges, { added, removed })
          transaction.removeMark(from, to, existing.mark)
          if (merged !== null) {
            const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, existing.baseAttrs), formattingChanges: merged }
            transaction.addMark(from, to, suggestionMarkType.create(attrs))
          }
          continue
        }

        const formatting: FormattingChanges = { added: Array.from(added), removed: Array.from(removed) }
        const baseAttrs = createBaseAttrs(user)
        const attrs = { ...buildSuggestionAttrs(SUGGESTION_TYPE_FORMATTING, baseAttrs), formattingChanges: formatting }
        transaction.addMark(from, to, suggestionMarkType.create(attrs))
      }

      transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
      return transaction
    },

    props: {
      handleKeyDown(view, event) {
        if (!options.getEnabled()) {
          return false
        }
        if (event.key !== 'Backspace' && event.key !== 'Delete') {
          return false
        }
        // Modifier-assisted shortcuts (e.g. Ctrl/Cmd-Backspace = delete word) span more
        // than one character. Let the default handler perform the deletion — it won't be
        // tracked, but the keystroke at least does what the user expects.
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
          return false
        }
        return interceptDeletion(view, event.key, options)
      },

      handleTextInput(view, from, to, text) {
        if (!options.getEnabled()) {
          return false
        }
        if (from === to) {
          return false
        }
        if (rangeIsInsideInsertion(view.state.doc, from, to)) {
          return false
        }

        const user = options.getCurrentUser()
        if (user === null) {
          return false
        }

        const suggestionMarkType = view.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
        if (suggestionMarkType === null) {
          return false
        }

        const baseAttrs = findAdjacentSuggestionAttrs(view.state.doc, from, to, user.id) ?? createBaseAttrs(user)

        const transaction = view.state.tr
        transaction.addMark(from, to, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_DELETION, baseAttrs)))

        const insertedTextNode = view.state.schema.text(text)
        transaction.insert(to, insertedTextNode)

        const insertedFrom = to
        const insertedTo = to + text.length
        transaction.addMark(insertedFrom, insertedTo, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_INSERTION, baseAttrs)))

        transaction.setSelection(TextSelection.create(transaction.doc, insertedTo))
        transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        view.dispatch(transaction)
        return true
      },

      handlePaste(view, _event, slice) {
        if (!options.getEnabled()) {
          return false
        }
        if (slice.size === 0) {
          return false
        }

        const { state } = view
        const { from, to } = state.selection

        // Pure insertion paste — let the default handler run; appendTransaction
        // will wrap the inserted slice as a regular insertion suggestion.
        if (from === to) {
          return false
        }

        // Pasting inside the user's own pending insertion just edits that insertion
        // — no separate deletion suggestion needed.
        if (rangeIsInsideInsertion(state.doc, from, to)) {
          return false
        }

        const user = options.getCurrentUser()
        if (user === null) {
          return false
        }

        const suggestionMarkType = state.schema.marks[SUGGESTION_MARK_NAME] ?? null
        if (suggestionMarkType === null) {
          return false
        }

        const baseAttrs = findAdjacentSuggestionAttrs(state.doc, from, to, user.id) ?? createBaseAttrs(user)

        const transaction = state.tr
        transaction.addMark(from, to, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_DELETION, baseAttrs)))

        // Insert the pasted slice immediately after the deletion-marked range. `replace`
        // (not `insert`) respects the slice's openStart/openEnd so block-level pastes
        // merge into the destination block the same way a default paste would.
        transaction.replace(to, to, slice)
        const insertedFrom = to
        const insertedTo = transaction.mapping.map(to, 1)

        // Strip any suggestion marks that came across with the pasted content so the
        // pasted range only carries OUR insertion mark.
        transaction.removeMark(insertedFrom, insertedTo, suggestionMarkType)
        transaction.addMark(insertedFrom, insertedTo, suggestionMarkType.create(buildSuggestionAttrs(SUGGESTION_TYPE_INSERTION, baseAttrs)))

        const resolvedCursor = transaction.doc.resolve(Math.min(insertedTo, transaction.doc.content.size))
        transaction.setSelection(TextSelection.between(resolvedCursor, resolvedCursor))
        transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        view.dispatch(transaction)
        return true
      },

      handleDOMEvents: {
        cut(view, event) {
          if (!options.getEnabled()) {
            return false
          }

          const { state } = view
          const { from, to } = state.selection

          if (from === to) {
            return false
          }

          if (rangeIsInsideInsertion(state.doc, from, to)) {
            return false
          }

          event.preventDefault()

          if (event.clipboardData !== null) {
            const text = state.doc.textBetween(from, to, '\n')
            event.clipboardData.setData('text/plain', text)

            const slice = state.doc.slice(from, to)
            const serializer = DOMSerializer.fromSchema(state.schema)
            const fragment = serializer.serializeFragment(slice.content)
            const container = document.createElement('div')
            container.appendChild(fragment)
            event.clipboardData.setData('text/html', container.innerHTML)
          }

          return markRangeAsDeletion(view, { from, to, cursorAfter: from }, options)
        },
      },
    },
  })
}
